// RawMaterialMovements.js - "Historique des mouvements" for M9 raw material
// stock, mirrors StockMovements.js (M7's finished-goods equivalent): full
// paginated ledger across ALL materials, filterable by material and type.
// Uses a searchable Autocomplete (not a plain <select>) for the material
// filter so it can be searched by typing any part of the name.
import React, { useEffect, useState } from "react";
import "../Stock/Stock.css";
import { ToastContainer, toast } from "react-toastify";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  TextField,
} from "@mui/material";
import { UilAngleLeft, UilAngleRight } from "@iconscout/react-unicons";
import { userRequest } from "../../RequestMethods";

const MOVEMENT_TYPES = ["in", "out", "reserve", "release", "correction"];

const RawMaterialMovements = () => {
  const [rawMaterials, setRawMaterials] = useState([]);
  const [movements, setMovements] = useState([]);
  const [movementsLoading, setMovementsLoading] = useState(true);
  const [movementsPagination, setMovementsPagination] = useState({ page: 1, limit: 25, total: 0, pages: 1 });
  const [filters, setFilters] = useState({ rawMaterialId: "", type: "" });

  const fetchRawMaterials = async () => {
    try {
      const res = await userRequest.get("raw-materials/dropdown");
      setRawMaterials(res.data.rawMaterials || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur lors du chargement des matières premières.");
    }
  };

  const fetchMovements = async (page = 1) => {
    try {
      setMovementsLoading(true);
      const params = { page, limit: movementsPagination.limit };
      if (filters.rawMaterialId) params.rawMaterialId = filters.rawMaterialId;
      if (filters.type) params.type = filters.type;

      const res = await userRequest.get("raw-materials/movements", { params });
      setMovements(res.data.movements || []);
      setMovementsPagination(res.data.pagination || { page: 1, limit: 25, total: 0, pages: 1 });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur lors du chargement des mouvements.");
    } finally {
      setMovementsLoading(false);
    }
  };

  useEffect(() => {
    fetchRawMaterials();
    fetchMovements(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyFilters = (e) => {
    e.preventDefault();
    fetchMovements(1);
  };

  const handlePrevPage = () => {
    if (movementsPagination.page > 1) fetchMovements(movementsPagination.page - 1);
  };

  const handleNextPage = () => {
    if (movementsPagination.page < movementsPagination.pages) fetchMovements(movementsPagination.page + 1);
  };

  return (
    <div className="Stock">
      <h1>Historique des mouvements (matières premières)</h1>

      <section className="stock-section">
        <form className="stock-filters" onSubmit={handleApplyFilters} style={{ alignItems: "center" }}>
          <Autocomplete
            options={rawMaterials}
            getOptionLabel={(m) => m.name || m._id}
            isOptionEqualToValue={(opt, val) => opt._id === val._id}
            renderOption={(optionProps, option) => {
              const { key, ...rest } = optionProps;
              return <li key={option._id} {...rest}>{option.name || option._id}</li>;
            }}
            value={rawMaterials.find((m) => m._id === filters.rawMaterialId) || null}
            onChange={(e, val) => setFilters({ ...filters, rawMaterialId: val ? val._id : "" })}
            renderInput={(params) => (
              <TextField {...params} label="Matière première" placeholder="Rechercher..." size="small" />
            )}
            sx={{ minWidth: 240 }}
          />
          <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
            <option value="">Tous les types</option>
            {MOVEMENT_TYPES.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          <button type="submit" className="action-icon-btn btn-primary">Filtrer</button>
        </form>

        {movementsLoading ? (
          <div className="loading-container">
            <div className="loading-spinner-custom"></div>
          </div>
        ) : (
          <>
            <TableContainer component={Paper} className="stock-table-container">
              <Table>
                <TableHead>
                  <TableRow className="table-header">
                    <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                    <TableCell>Matière première</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Quantité</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Par</TableCell>
                    <TableCell>Raison</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {movements.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">Aucun mouvement trouvé.</TableCell>
                    </TableRow>
                  )}
                  {movements.map((movement) => {
                    const material = movement.rawMaterialStockId?.rawMaterialId;
                    return (
                      <TableRow key={movement._id}>
                        <TableCell>{new Date(movement.createdAt).toLocaleString("fr-FR")}</TableCell>
                        <TableCell>{material?.name || "—"}{material?.unit ? ` (${material.unit})` : ""}</TableCell>
                        <TableCell>
                          <span className={`badge movement-${movement.type}`}>{movement.type}</span>
                        </TableCell>
                        <TableCell>{movement.quantity > 0 ? `+${movement.quantity}` : movement.quantity}</TableCell>
                        <TableCell>{movement.sourceType}</TableCell>
                        <TableCell>
                          {movement.performedBy ? `${movement.performedBy.name || ""} ${movement.performedBy.surname || ""}`.trim() : "—"}
                        </TableCell>
                        <TableCell>{movement.reason || "—"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <div className="stock-pagination">
              <button
                className="pagination-btn" onClick={handlePrevPage}
                disabled={movementsPagination.page <= 1}
              >
                <UilAngleLeft /> Précédent
              </button>
              <span>
                Page {movementsPagination.page} / {movementsPagination.pages || 1} ({movementsPagination.total} entrées)
              </span>
              <button
                className="pagination-btn" onClick={handleNextPage}
                disabled={movementsPagination.page >= movementsPagination.pages}
              >
                Suivant <UilAngleRight />
              </button>
            </div>
          </>
        )}
      </section>

      <ToastContainer position="top-right" />
    </div>
  );
};

export default RawMaterialMovements;
